/**
 * AuthMethodSelector — Pill toggle for authentication method selection.
 * Keyboard navigation: Arrow keys cycle, Home/End jump, Enter/Space select.
 *
 * ## It composes `<TogglePillGroup>` (R-52 follow-up, 2026-08-14)
 *
 * This component and `@puffin-core/re-auth`'s `AuthMethodSelector` were
 * near-COPIES of each other, and both hand-wrote the `.toggle-pill` chrome that
 * planner-web's two pills also hand-wrote — four implementations of one control.
 * The chrome, the ARIA and the roving keyboard now live once in
 * `@puffin-core/page-shell`; what stays here is the only thing that is
 * genuinely login's: which methods exist, how they are labelled, and the
 * passkey-specific affordances.
 *
 * ⚠️ Imported through the `./toggle-pill-group` SUBPATH rather than the package
 * barrel, following the `./section-pill` precedent — a login surface has no
 * business pulling page-shell's whole export graph to draw three buttons.
 *
 * @module @puffin-core/login-shell/components/AuthMethodSelector
 */

import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { TogglePillGroup } from '@puffin-core/page-shell/toggle-pill-group';
import { useLoginTranslate } from '../hooks/useLoginTranslate.js';

/** No method is unavailable: a stable default, so the options are not rebuilt on every render. */
const NO_UNAVAILABLE_METHODS = Object.freeze([]);

const BUILT_IN_METHODS = {
  email_password: { labelKey: 'method.emailPassword' },
  passkey: { labelKey: 'method.passkey' },
  pin: { labelKey: 'method.pin' },
};

/**
 * The catalog key that names a method — the selector's pill, and anything that names a method beside it.
 *
 * @param {string} method
 * @returns {string}
 */
export function methodLabelKey(method) {
  return (BUILT_IN_METHODS[method] || { labelKey: `method.${method}` }).labelKey;
}

export function AuthMethodSelector({
  selectedMethod,
  onMethodChange,
  methods,
  t,
  ariaLabel,
  passkeySupported = true,
  passkeyStatusLoading = false,
  platformAuthenticatorAvailable = false,
  unavailableMethods = NO_UNAVAILABLE_METHODS,
  unavailableHintId,
}) {
  const methodIds = useMemo(() => methods ?? Object.keys(BUILT_IN_METHODS), [methods]);

  // ⚠️ RECIPE §3 hook placement: `useLoginTranslate` is a CUSTOM hook, and
  // `web/component-state-order` requires custom hooks AFTER computed ones.
  const translate = useLoginTranslate(t);

  const options = methodIds.map((method) => {
    const isPasskey = method === 'passkey';
    // A passkey on a device with no authenticator is UNAVAILABLE, not
    // refusing-with-a-reason — so `disabled`, not `inert`.
    const disabled = isPasskey && passkeySupported === false;
    return {
      value: method,
      label: translate(methodLabelKey(method)),
      isActive: selectedMethod === method,
      disabled,
      // Unavailable NOW, for a reason the form states (a locked register that cannot reach the server holds no recent
      // proof of this kind — POS-OFFLINE-STATION-LOCK-001 U14'd-2): reachable and announced, never hidden.
      inert: unavailableMethods.includes(method),
      badge:
        isPasskey && passkeyStatusLoading ? (
          <span className="toggle-pill-badge toggle-pill-badge-info">
            {translate('method.checking')}
          </span>
        ) : null,
      dataAttrs: {
        'data-method': method,
        'data-biometric':
          isPasskey && passkeySupported && platformAuthenticatorAvailable ? 'true' : undefined,
      },
      onSelect: () => onMethodChange(method),
    };
  });

  return (
    <TogglePillGroup
      variant="tab"
      className="mb-lg"
      options={options}
      ariaLabel={ariaLabel || translate('method.selectorLabel')}
      testId="auth-method-selector"
      optionTestId={(option) => `auth-method-${option.value}`}
      inertHint={unavailableMethods.length > 0 ? translate('method.unavailableHint') : undefined}
      inertHintId={unavailableHintId}
    />
  );
}

AuthMethodSelector.propTypes = {
  selectedMethod: PropTypes.string.isRequired,
  onMethodChange: PropTypes.func.isRequired,
  methods: PropTypes.arrayOf(PropTypes.string),
  t: PropTypes.func,
  ariaLabel: PropTypes.string,
  passkeySupported: PropTypes.bool,
  passkeyStatusLoading: PropTypes.bool,
  platformAuthenticatorAvailable: PropTypes.bool,
  /** Methods offered but refusing selection now; the form shows why (`LoginForm` `unavailableMethods`). */
  unavailableMethods: PropTypes.arrayOf(PropTypes.string),
  /** The id the selector's "not available now" hint renders under. Required with `unavailableMethods`. */
  unavailableHintId: PropTypes.string,
};

export default AuthMethodSelector;
